import React, { useState } from 'react';
import { Badge, Button, Spinner } from 'react-bootstrap';
import { useStoreActions } from 'easy-peasy';
import PropTypes from 'prop-types';
import AuthorizationChecker from '../../../../services/AuthorizationChecker.js';
import HelperMetiers360 from '../../../../services/HelpersMetiers360.js';
import './ClientAnomaliesComponent.scss';

const PRIORITY_VARIANT = { HIGH: 'danger', MEDIUM: 'warning', LOW: 'secondary' };
const PRIORITY_LABEL = { HIGH: 'Haute', MEDIUM: 'Moyenne', LOW: 'Basse' };
const CATEGORY_LABEL = { PIPEDRIVE: 'PipeDrive', SUBSCRIPTIONS: 'Abonnements', INTEGRITY: 'Intégrité' };

const ClientAnomaliesComponent = ({ anomalies, uniqueId, username }) => {
    const { resolveClientAnomaly } = useStoreActions(actions => actions.clients);
    const [resolvingCode, setResolvingCode] = useState(null);

    if (!anomalies?.length) return null;

    const handleResolve = (code) => {
        setResolvingCode(code);
        resolveClientAnomaly({ uniqueId, code, resolvedBy: username })
            .finally(() => setResolvingCode(null));
    };

    return (
        <div className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-2">
                <i className="fas fa-exclamation-triangle text-warning" />
                <strong className="small text-muted">
                    {anomalies.length === 1 ? '1 anomalie détectée' : `${anomalies.length} anomalies détectées`}
                </strong>
            </div>
            <div className="border rounded overflow-hidden">
                {anomalies.map((anomaly, index) => (
                    <div key={anomaly.code}
                        className={`anomaly-item priority-${anomaly.priority ?? 'LOW'} p-2${index < anomalies.length - 1 ? ' border-bottom' : ''}`}>

                        {/* Header : badges à gauche, date à droite */}
                        <div className="d-flex justify-content-between align-items-start mb-1">
                            <div className="d-flex align-items-center flex-wrap gap-1">
                                <span className="fw-bold small text-dark">{anomaly.code}</span>
                                <span className="small">{anomaly.label}</span>
                                <Badge bg={PRIORITY_VARIANT[anomaly.priority] ?? 'secondary'} className="anomaly-item__badge">
                                    {PRIORITY_LABEL[anomaly.priority] ?? anomaly.priority}
                                </Badge>
                                <Badge bg="light" text="dark" className="anomaly-item__badge border">
                                    {CATEGORY_LABEL[anomaly.category] ?? anomaly.category}
                                </Badge>
                                {anomaly.resolvedManuallyBy && (
                                    <Badge bg="success" className="anomaly-item__badge">
                                        <i className="fas fa-check me-1" />
                                        Traité par {anomaly.resolvedManuallyBy}
                                        {anomaly.resolvedManuallyAt &&
                                            <> · {HelperMetiers360.getdisplayDateType(anomaly.resolvedManuallyAt, 'day')}</>}
                                    </Badge>
                                )}
                            </div>
                            <span className="anomaly-item__date text-muted text-nowrap ms-3">
                                Détecté le {HelperMetiers360.getdisplayDateType(anomaly.detectedAt, 'day')}
                            </span>
                        </div>

                        {/* Description à gauche, bouton à droite */}
                        <div className="d-flex justify-content-between align-items-start gap-2">
                            <div className="anomaly-item__description text-muted">
                                {anomaly.description}
                            </div>
                            {AuthorizationChecker.isAdmin() && !anomaly.resolvedManuallyBy && (
                                <Button size="sm" variant="outline-secondary"
                                    className="anomaly-item__resolve-btn flex-shrink-0 py-0 px-2"
                                    disabled={resolvingCode === anomaly.code}
                                    onClick={() => handleResolve(anomaly.code)}>
                                    {resolvingCode === anomaly.code
                                        ? <Spinner as="span" animation="border" size="sm" />
                                        : <><i className="fas fa-check me-1" />Traité</>
                                    }
                                </Button>
                            )}
                        </div>

                        {anomaly.details && Object.keys(anomaly.details).length > 0 && (
                            <div className="anomaly-item__details text-muted mt-1">
                                {Object.entries(anomaly.details).map(([k, v]) => (
                                    <span key={k} className="me-3">
                                        {k}&nbsp;: <strong className="text-dark">{String(v)}</strong>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

ClientAnomaliesComponent.propTypes = {
    anomalies: PropTypes.array.isRequired,
    uniqueId: PropTypes.string.isRequired,
    username: PropTypes.string,
};

export default ClientAnomaliesComponent;
